/* istanbul ignore file */
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  Action,
  ActionCreator,
  Creator,
  MemoizedSelector,
  Store,
} from '@ngrx/store';
import { merge, NEVER, of } from 'rxjs';
import { exhaustMap, filter, take } from 'rxjs/operators';
import { LoadingSpinnerService } from '@arc/unified-payments-frontend/shared-payment';

/**
 * Factory that creates a reusable non-dispatching NgRx functional effect that
 * shows a loading spinner from the moment the platform JWT is obtained until
 * user info has been loaded and permissions are ready (success or error).
 *
 * Pass completeSelector (e.g. UserPermissionStore) to complete the spinner when
 * a store value becomes non-empty, in addition to any completeActions.
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiLoadUntilLoadUserEffect =
 *     createLoadUntilLoadUserEffect({
 *       triggerAction: MakePaymentPaymentLinksApiActions.platformJwtObtained,
 *       completeActions: [MakePaymentAuthApiActions.loadUserError],
 *       completeSelector: UserPermissionStore,
 *     });
 */
export function createLoadUntilLoadUserEffect<
  A extends Action<string>,
>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  completeActions: ActionCreator<string, Creator>[];
  completeSelector?: MemoizedSelector<object, any>;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      loadingSpinnerService = inject(LoadingSpinnerService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        exhaustMap(() => {
          const actionComplete$ = actions$.pipe(
            ofType(...config.completeActions),
            take(1),
          );
          const selectorComplete$ = config.completeSelector
            ? store.select(config.completeSelector).pipe(
                filter((value) => !!value?.length),
                take(1),
              )
            : NEVER;
          loadingSpinnerService.loadUntil(
            merge(actionComplete$, selectorComplete$).pipe(take(1)),
          );
          return of();
        }),
      ),
    { functional: true, dispatch: false },
  );
}

/**
 * Factory that creates a reusable non-dispatching NgRx functional effect that
 * shows a loading spinner from when the payment preview is updated until the
 * session ownership update completes (success or error).
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiLoadUntilSessionOwnershipEffect =
 *     createLoadUntilSessionOwnershipUpdatedEffect({
 *       triggerAction: MakePaymentPaymentInformationApiActions.paymentPreviewUpdated,
 *       completeActions: [
 *         MakePaymentSessionApiActions.sessionOwnershipUpdated,
 *         MakePaymentSessionApiActions.sessionOwnershipUpdateError,
 *       ],
 *     });
 */
export function createLoadUntilSessionOwnershipUpdatedEffect<
  A extends Action<string>,
>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  completeActions: ActionCreator<string, Creator>[];
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      loadingSpinnerService = inject(LoadingSpinnerService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        exhaustMap(() => {
          const completeActions$ = actions$.pipe(
            ofType(...config.completeActions),
            take(1),
          );
          loadingSpinnerService.loadUntil(completeActions$);
          return of();
        }),
      ),
    { functional: true, dispatch: false },
  );
}
